/**
 * @fileoverview Shared hook for calculating generation costs.
 *
 * Handles video (per-second), image (per-image), and fixed/tiered pricing,
 * including audio surcharges and resolution multipliers. Web-search surcharges
 * are intentionally absorbed (not charged to users).
 */

import { useMemo } from 'react'
import { getStorageUrl } from '@/lib/api'
import type { ExpectedCustomerPricing, ModelInfo, ProjectModelPricingContext } from '@/types'
import {
  MODEL_OLIVE_COSTS,
  billableReferenceImageUrls,
  referenceAudioTokens,
  referenceImageTokens,
  referenceVideoTokens,
  resolveReferenceTokenSurchargeOlives,
  type ReferenceTokenTerms,
  type ReferenceImageMeasurement,
  type PricedModelId,
  replaceModelOliveCost,
  resolveAudioSurchargeOlives,
  resolveImageOlivesPerAsset,
  resolveOlivesForChars,
  resolveOlivesForDuration,
  resolveReferenceImageSurchargeOlives,
  resolutionStringToHeight,
} from '@/types/shared'
import {
  CustomerPricingNeedsServerQuoteError,
  applyCustomerPricingRule,
  estimateCostPlusCharge,
  pickCustomerPricingRules,
  quoteWithCustomerPricingRules,
  type CustomerBillableUnit,
  type CustomerListCostBreakdown,
  type ApplyCustomerPricingOptions,
  type CustomerPriceQuote,
  type CustomerPricingTerms,
  type CustomerPricingRule,
} from '@/types/customer-pricing'
import type { UseBYOKPricingResult } from '@/lib/hooks/useBYOKPricing'

/** Estimate from board geometry; assets awaiting dimensions use the estimate-only 16:9 assumption. */
export function estimateReferenceMeasurements(
  input: Parameters<typeof billableReferenceImageUrls>[0],
  assets: Iterable<{ uri: string; enhancedUri?: string; thumbnailUri?: string; width?: number; height?: number }>,
  terms: ReferenceTokenTerms | undefined,
): Array<Pick<ReferenceImageMeasurement, 'tokens'>> {
  if (!terms) return []
  const byUrl = new Map<string, { width?: number; height?: number }>()
  for (const asset of assets) {
    for (const uri of [asset.uri, asset.enhancedUri, asset.thumbnailUri]) {
      if (uri) byUrl.set(getStorageUrl(uri), asset)
    }
  }
  return billableReferenceImageUrls({ ...input, maxImages: terms.maxImages }).map(url => ({
    tokens: referenceImageTokens(byUrl.get(getStorageUrl(url)), terms),
  }))
}

/**
 * Seconds of one reference audio clip as the worker sends it (`generation/index.ts`, mirrored
 * by `measureReferenceAudio` at admission): the trim window when set, else the source up to
 * the model's combined cap. An unknown source length yields 0, which `referenceAudioTokens`
 * prices at the card's fallback — the same cap — never at zero.
 */
export function referenceAudioClipSecondsForBilling(
  clip: { trimDurationSec?: number | null; sourceSeconds?: number | null },
  maxCombinedDurationSec: number | null | undefined,
): number {
  const positive = (value: number | null | undefined) =>
    typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined
  const trim = positive(clip.trimDurationSec)
  if (trim !== undefined) return trim
  const source = positive(clip.sourceSeconds)
  if (source === undefined) return 0
  const cap = positive(maxCombinedDurationSec)
  return cap === undefined ? source : Math.min(source, cap)
}

export interface UseCostEstimationOptions {
  /**
   * Selected model (null if no model selected)
   */
  model: ModelInfo | null

  /**
   * Modality type (determines which parameters to use)
   */
  modality: 'video' | 'image' | 'audio'

  /**
   * Number of items to generate (1, 2, or 4)
   */
  batchCount: number

  // Video-specific parameters
  /**
   * Video duration in seconds (for per-second pricing)
   */
  duration?: number

  /** Per-second quality branch (e.g. FLUX.3 Draft or Full), applied before resolution tiers. */
  perSecondQuality?: string | null

  /**
   * Whether audio is enabled (for audio surcharge)
   */
  audioEnabled?: boolean
  voiceIds?: string[]

  /**
   * Input text length in characters (for per-1,000-character TTS pricing).
   * Feed the live prompt length so the estimate updates as the user types.
   */
  charCount?: number

  /**
   * Number of video references attached to the request. Mirrors the backend
   * billing rule: when > 0 and the model declares `pricing.videoInputDiscount`,
   * the per-second rate is multiplied by the reference rate (e.g., Seedance 2.0 → 0.9×).
   */
  videoReferenceCount?: number
  /**
   * A source video drives this request (audio models such as Sonilo): routes
   * the BYOK estimate to the model's reference endpoint like a reference would.
   */
  sourceVideoPresent?: boolean
  /** Combined trimmed duration, in seconds, of video references sent to the provider. */
  videoReferenceDuration?: number

  /**
   * Distinct billable reference images for models declaring
   * `pricing.referenceImageSurcharge` (H3: first 5 free, then per-image).
   * Callers compute this with `countBillableReferenceImages` over the SAME
   * gated fields the submit sends — start-frame mode strips references, and
   * repeated URLs collapse — so the quoted surcharge matches the charge.
   */
  imageReferenceCount?: number
  imageReferenceMeasurements?: readonly Pick<ReferenceImageMeasurement, 'tokens'>[]
  /**
   * Seconds of each reference audio clip the provider will receive — the trim window, else the
   * source up to the model's combined cap (`referenceAudioClipSecondsForBilling`; 0 when the
   * source length is unknown, priced at the card's fallback). Cards with
   * `referenceTokenSurcharge.tokensPerAudioSecond` tokenize them against the same allowance
   * as the images, and a cost-based rule charges them at cost.
   */
  audioReferenceSeconds?: readonly number[]
  /**
   * Seconds of each reference video clip the provider will receive (trim window, else the
   * source length). Cards with `referenceTokenSurcharge.videoSamplesPerFrame` tokenize them at
   * the OUTPUT resolution against the shared allowance; the browser assumes 24 fps, the server
   * measures the source's own rate.
   */
  videoReferenceClipSeconds?: readonly number[]
  /** Output aspect ratio, for provider cost cards that price square outputs above 16:9. */
  aspectRatio?: string | null

  /**
   * Whether the request the panel would submit carries any media input —
   * computed by the caller with `seedanceHasMediaInputs` over the SAME
   * gated fields the submit sends (frames, image/video references, elements).
   * On models exposing `pricing.mediaInputPricing` (route-split Seedance,
   * ENG-755) this swaps the estimate to the fal card, matching the per-request
   * provider the backend charge resolves. Ignored for other models.
   */
  mediaInputsPresent?: boolean

  // Image-specific parameters
  /**
   * Resolution setting (for resolution-based multipliers like 4K = 2x)
   */
  resolution?: string | null

  /**
   * Quality tier (e.g., 'low', 'medium', 'high') for per-image models that
   * expose quality (currently only GPT Image 2). Lower tiers cost a fraction
   * of the base price to reflect dramatically lower provider cost.
   */
  quality?: string | null

  /**
   * Whether web search is enabled. Kept for API symmetry with the generation
   * form state; the web-search provider cost is currently absorbed by Martini
   * and is not reflected in the returned cost. Callers should still pass this
   * through if/when pricing changes.
   */
  enableWebSearch?: boolean | null

  /**
   * Optional BYOK pricing state. When present and the selected model's provider
   * is direct-billed, the hook switches to direct billing display mode.
   */
  byokPricing?: UseBYOKPricingResult
}

/**
 * - `olives`: Martini's key, charged in olives.
 * - `byok_usd`: the org's own provider key pays the provider; no olives.
 * - `fal_key_required`: the org's fal-key plan bills this model to its fal key, and none is
 *   saved yet. Generating asks for the key (`FAL_KEY_REQUIRED`); no olives are charged.
 */
export type CostDisplayMode = 'olives' | 'byok_usd' | 'fal_key_required'

/** The guard a standard-priced request sends: no negotiated rule. */
export const STANDARD_CUSTOMER_PRICING: ExpectedCustomerPricing = { ruleId: null, ruleVersion: null }

/**
 * What a request needs a rule to price. An absolute rule that lacks a term the
 * request needs (a video-reference rate, or the request's unit) does not match
 * and selection falls through to the next scope — the same rule the shared
 * engine applies to a full breakdown (`customerPricingRuleMatches`).
 */
export interface CustomerPricingMatch {
  /** Omit when unknown (contextual models absent from the catalog); unit is then not checked. */
  unit?: CustomerBillableUnit
  hasVideoReferences?: boolean
}

function billableUnitOf(model: ModelInfo | null | undefined): CustomerBillableUnit | undefined {
  switch (model?.pricing?.type) {
    case 'per_second':
      return 'second'
    case 'per_image':
      return 'image'
    case 'fixed':
      return 'asset'
    default:
      return undefined
  }
}

/** Whether a list card charges for video references at all (Wan accepts them for free; H3 bills the clip's seconds). */
export function cardBillsVideoReferences(pricing: ModelInfo['pricing'] | null | undefined): boolean {
  if (!pricing) return false
  return (
    pricing.videoInputOlivesPerSecond != null ||
    pricing.videoInputDiscount != null ||
    Boolean(pricing.resolutionTiers?.some(tier => tier.videoInputOlivesPerSecond != null)) ||
    cardBillsVideoReferences(pricing.mediaInputPricing as ModelInfo['pricing'] | undefined)
  )
}

function ruleCanPrice(rule: CustomerPricingRule, match: CustomerPricingMatch): boolean {
  if (rule.kind !== 'absolute') return true
  if (match.unit !== undefined && rule.unit !== match.unit) return false
  if (match.hasVideoReferences && !rule.videoReference) return false
  return true
}

export function resolveCustomerPricingRules(model: ModelInfo | null | undefined): CustomerPricingRule[] {
  return model?.customerPricing?.rules ?? []
}

/**
 * The rule that would price a request against `source` (a catalog model or a
 * project pricing context's rules), as the `expectedPricing` guard `/generate`
 * revalidates. Nulls mean standard pricing.
 */
export function resolveCustomerPricingGuard(
  source: ModelInfo | readonly CustomerPricingRule[] | null | undefined,
  resolution?: string | null,
  match: CustomerPricingMatch = {},
): ExpectedCustomerPricing {
  const rules = Array.isArray(source) ? source : resolveCustomerPricingRules(source as ModelInfo | null | undefined)
  const model = Array.isArray(source) ? undefined : (source as ModelInfo | null | undefined)
  const effectiveMatch: CustomerPricingMatch = {
    ...match,
    ...(model && match.unit === undefined ? { unit: billableUnitOf(model) } : {}),
    // A reference request only needs a reference term when the card bills references.
    ...(model && match.hasVideoReferences ? { hasVideoReferences: cardBillsVideoReferences(model.pricing) } : {}),
  }
  const rule = pickCustomerPricingRules(rules, resolution).find(candidate => ruleCanPrice(candidate, effectiveMatch))
  return rule ? { ruleId: rule.id, ruleVersion: rule.version } : STANDARD_CUSTOMER_PRICING
}

/**
 * A quote as the browser holds it. A cost-based rule priced from the catalog's customer rates is an
 * estimate: it names itself as one instead of borrowing the server's `cost_plus` terms (which record
 * our provider cost) or the `standard` terms of a price it does not charge.
 */
export type BrowserPriceQuote = Omit<CustomerPriceQuote, 'terms'> & {
  terms: CustomerPricingTerms | { kind: 'cost_plus_estimate'; surcharges: number }
}

export interface BrowserCustomerPriceQuote {
  quote: BrowserPriceQuote
  /**
   * The selected rule prices from provider cost and the catalog carried no
   * customer rates for the model (`customerPricing.costPlus`): `quote` charges the
   * standard list cost, and the exact charge is the server's. Label the number
   * instead of presenting it as the charge.
   */
  pricedByAgreement: boolean
}

/**
 * `quoteWithCustomerPricingRules` for the browser. A cost-based rule without
 * `costPlusCharge` cannot be priced here, so its quote is the list cost — still carrying the rule's
 * id/version, because `/generate` revalidates the guard against the rule the
 * server selects and would answer 409 PRICING_CHANGED to a standard guard.
 */
export function quoteCustomerPriceInBrowser(
  breakdown: CustomerListCostBreakdown,
  rules: readonly CustomerPricingRule[],
  options: ApplyCustomerPricingOptions = {},
  /** The cost-based rule's charge from the catalog's customer rates, when they can price the request. */
  costPlusCharge: number | null = null,
): BrowserCustomerPriceQuote {
  try {
    return { quote: quoteWithCustomerPricingRules(breakdown, rules, options), pricedByAgreement: false }
  } catch (error) {
    if (!(error instanceof CustomerPricingNeedsServerQuoteError)) throw error
    const { id, version, scope } = error.rule
    const list = applyCustomerPricingRule(breakdown, null)
    const guard = { pricingRuleId: id, pricingRuleVersion: version, scope }
    if (costPlusCharge == null) return { quote: { ...list, ...guard }, pricedByAgreement: true }
    return {
      quote: {
        ...list,
        ...guard,
        finalCharge: costPlusCharge,
        multiplier: list.canonicalCost > 0 ? roundOlives(costPlusCharge / list.canonicalCost) : 1,
        terms: { kind: 'cost_plus_estimate', surcharges: roundOlives(breakdown.surcharges) },
      },
      pricedByAgreement: false,
    }
  }
}

/** Reads the guard off a quote the shared engine produced. */
export function expectedPricingFromQuote(
  quote: Pick<CustomerPriceQuote, 'pricingRuleId' | 'pricingRuleVersion'> | null | undefined,
): ExpectedCustomerPricing {
  return quote ? { ruleId: quote.pricingRuleId, ruleVersion: quote.pricingRuleVersion } : STANDARD_CUSTOMER_PRICING
}

export interface ResolvedProjectModelPricing {
  /** The organization's effective rules for the model; quote with the shared engine. */
  rules: CustomerPricingRule[]
  /** The rule the request would be priced against, for the `/generate` guard. */
  expectedPricing: ExpectedCustomerPricing
}

/**
 * Resolve the pricing contract shared by contextual UI actions and /generate.
 * Negotiated pricing is safe to quote and guard only when the project response
 * exposes it. Link-shared writers receive the canonical price they are charged,
 * without receiving the billing organization's private rules.
 */
export function resolveProjectModelPricing(
  context: ProjectModelPricingContext | undefined,
  modelId: string,
  resolution?: string | null,
  match: CustomerPricingMatch = {},
): ResolvedProjectModelPricing | null {
  if (!context) return null
  if (context.mode === 'canonical') return { rules: [], expectedPricing: STANDARD_CUSTOMER_PRICING }
  const rules = context.byModelId[modelId]?.rules ?? []
  return { rules, expectedPricing: resolveCustomerPricingGuard(rules, resolution, match) }
}

function roundOlives(value: number): number {
  return Math.round(value * 10_000) / 10_000
}

export interface UseCostEstimationResult {
  /**
   * Total estimated cost in olives (null if model not selected)
   */
  estimatedCost: number | null

  /**
   * Total estimated cost in USD — set only when `mode === 'byok_usd'`.
   * null means BYOK is active but the pricing fetch couldn't resolve a unit
   * price; callers should render a `—` placeholder.
   */
  estimatedUsd: number | null

  /** Reference tokens past the card's included allowance, which the provider bills per 1,000. */
  billedReferenceTokens?: number

  /**
   * `olives` for normal (Martini-billed) pricing, `byok_usd` when the active
   * org's BYOK provider key is paying the provider directly, `fal_key_required`
   * when that key is still to be added (see CostDisplayMode).
   */
  mode: CostDisplayMode

  /**
   * Cost breakdown for transparency. Surcharges/discounts are reported relative
   * to the un-multiplied `base` price; `batch` is the final total after batch
   * count, including all multipliers and surcharges.
   */
  costBreakdown: {
    base: number
    audio?: number
    /** Flat olives for reference images beyond the model's included allowance. */
    referenceImages?: number
    /** Additional cost from the resolution multiplier (e.g. 4K = +base). */
    resolution?: number
    /** Negative when a quality multiplier discounts the base (e.g. low = -0.75 × base). */
    quality?: number
    batch: number
  } | null

  /**
   * The negotiated (or standard) price of ONE batch item from the shared
   * customer-pricing engine — `pricingRuleId`/`pricingRuleVersion` are the
   * `expectedPricing` guard to send with the request. null without a model.
   */
  quote: BrowserPriceQuote | null

  /**
   * True when the organization's cost-based agreement prices this request and
   * the browser could not (no customer rates for the model). The estimate is then
   * the standard list cost, not the charge, so label it rather than showing it as the price.
   */
  pricedByAgreement: boolean
}

/**
 * Pure cost calculation — no hooks, no BYOK. Used when callers need to tally
 * costs across many items (e.g., batch panels) without calling a hook in a loop.
 * Returns olives-mode only; BYOK mode is layered on inside `useCostEstimation`.
 */
export function computeEstimatedCost(options: UseCostEstimationOptions): UseCostEstimationResult {
  const {
    model,
    batchCount,
    duration,
    perSecondQuality,
    audioEnabled,
    voiceIds,
    charCount,
    resolution,
    quality,
    videoReferenceCount,
    videoReferenceDuration,
    audioReferenceSeconds,
    videoReferenceClipSeconds,
    imageReferenceCount,
    imageReferenceMeasurements,
  } = options

  if (!model) {
    return {
      estimatedCost: null,
      estimatedUsd: null,
      mode: 'olives',
      costBreakdown: null,
      quote: null,
      pricedByAgreement: false,
    }
  }

  // Route-split Seedance (ENG-755): a media-input-bearing request routes to
  // fal and bills the fal card, so the quote must swap cards with it. Whole-
  // card replacement, not a spread — the cards use disjoint reference-pricing
  // fields (videoInputDiscount vs videoInputOlivesPerSecond).
  const servedPricing =
    model.pricing.mediaInputPricing && options.mediaInputsPresent
      ? replaceModelOliveCost(model.pricing, model.pricing.mediaInputPricing)
      : model.pricing
  /** The list-price decomposition of one item on a given rate card. */
  const listCosts = (pricing: ModelInfo['pricing']) => {
    let baseCost = 0
    let variableCost = 0
    let audioSurcharge = 0
    let referenceImageSurcharge = 0
    let referenceTokenSurchargeOlives = 0
    let billedReferenceTokens = 0
    let resolutionAdjustment = 0
    let qualityAdjustment = 0
    // The list-cost decomposition a negotiated rule prices: output quantity in
    // the model's unit, the reference seconds the list card bills on top, and
    // the surcharges an absolute rule adds back at list.
    let billableUnit: CustomerBillableUnit = 'asset'
    let outputQuantity = 1
    let videoReferenceQuantity = 0
    const hasVideoReferences = (videoReferenceCount ?? 0) > 0
    // Whether the list card charges for those references (H3 and Wan do not).
    let billsVideoReferences = false

    if (pricing.type === 'per_second') {
      const qualityTier = perSecondQuality ? pricing.perSecondQualityTiers?.[perSecondQuality] : undefined
      let costPerSecond = qualityTier?.olivesPerSecond ?? pricing.olivesPerSecond ?? 0
      let videoInputCostPerSecond = pricing.videoInputOlivesPerSecond
      // A declared quality tier REPLACES the rate card; it does not inherit the
      // base card's resolution matrix. FLUX.3 Draft is a flat fixed-720p rate, so
      // inheriting here quoted every Draft render at the Full 720p rate (3x).
      const resolutionTiers = qualityTier ? qualityTier.resolutionTiers : pricing.resolutionTiers

      // Resolution-based tiers (e.g. LTX 2.3 charges more for 1440p/2160p output).
      if (resolutionTiers && resolution) {
        const height = resolutionStringToHeight(resolution)
        if (height !== undefined) {
          for (const tier of resolutionTiers) {
            if (height <= tier.maxHeight) {
              costPerSecond = tier.olivesPerSecond
              videoInputCostPerSecond = tier.videoInputOlivesPerSecond ?? videoInputCostPerSecond
              break
            }
          }
        }
      }

      // Native ModelArk publishes an exact combined input/output rate. Other
      // providers can continue using a relative video-input discount.
      billsVideoReferences = videoInputCostPerSecond != null || pricing.videoInputDiscount != null
      if (hasVideoReferences && videoInputCostPerSecond != null) {
        costPerSecond = videoInputCostPerSecond
      } else if (pricing.videoInputDiscount != null && hasVideoReferences) {
        costPerSecond *= pricing.videoInputDiscount
      }

      const billedReferenceDuration =
        (videoInputCostPerSecond != null || pricing.videoInputDiscount != null) &&
        hasVideoReferences &&
        typeof videoReferenceDuration === 'number'
          ? videoReferenceDuration
          : 0
      const billedDuration = (duration ?? 0) + billedReferenceDuration
      baseCost = costPerSecond * billedDuration
      variableCost = baseCost
      billableUnit = 'second'
      outputQuantity = duration ?? 0
      videoReferenceQuantity = billedReferenceDuration

      const audioSurchargePerSecond = resolveAudioSurchargeOlives(
        pricing,
        resolution ? resolutionStringToHeight(resolution) : undefined,
      )
      if (audioEnabled && audioSurchargePerSecond) {
        audioSurcharge = audioSurchargePerSecond * (duration ?? 0)
      }
      if (audioEnabled && voiceIds?.length && pricing.voiceSurchargeOlives) {
        audioSurcharge += pricing.voiceSurchargeOlives * (duration ?? 0)
      }
      const measurements =
        imageReferenceMeasurements ??
        Array.from(
          { length: Math.min(imageReferenceCount ?? 0, pricing.referenceTokenSurcharge?.maxImages ?? 0) },
          () => ({ tokens: pricing.referenceTokenSurcharge!.estimateTokensPerUnmeasuredImage }),
        )
      const terms = pricing.referenceTokenSurcharge
      // Audio clips share the image allowance (referenceTokenCost.ts on the server); the olive
      // card absorbs them, so they reach only the cost-based charge through the billed count.
      const audioTokens = terms
        ? (audioReferenceSeconds ?? []).reduce((sum, seconds) => sum + referenceAudioTokens(seconds, terms), 0)
        : 0
      const videoTokens = terms
        ? (videoReferenceClipSeconds ?? []).reduce(
            (sum, seconds) =>
              sum +
              referenceVideoTokens({ seconds }, resolution ? resolutionStringToHeight(resolution) : undefined, terms),
            0,
          )
        : 0
      // One pooled total, as the server measures it: the list card surcharges everything past
      // the allowance, and a cost-based rule prices the same billed count at cost.
      const measuredReferenceTokens = terms
        ? measurements.slice(0, terms.maxImages).reduce((sum, image) => sum + image.tokens, 0) +
          audioTokens +
          videoTokens
        : 0
      billedReferenceTokens = terms ? Math.max(0, measuredReferenceTokens - terms.includedTokens) : 0
      referenceTokenSurchargeOlives = resolveReferenceTokenSurchargeOlives(pricing, measuredReferenceTokens)
      referenceImageSurcharge =
        resolveReferenceImageSurchargeOlives(pricing, imageReferenceCount ?? 0) + referenceTokenSurchargeOlives
    } else if (pricing.type === 'per_image') {
      baseCost = pricing.olivesPerAsset ?? 0
      variableCost = resolveImageOlivesPerAsset(pricing, { resolution, quality })
      const resolutionOnlyCost = resolveImageOlivesPerAsset(pricing, { resolution })
      resolutionAdjustment = resolutionOnlyCost - baseCost
      qualityAdjustment = variableCost - resolutionOnlyCost
      billableUnit = 'image'
    } else if (pricing.type === 'fixed') {
      if (pricing.olivesPerThousandChars != null) {
        // TTS / voiceover — billed per 1,000 characters of input text.
        baseCost = resolveOlivesForChars(pricing, charCount ?? 0)
      } else {
        // `resolveOlivesForDuration` covers all duration-aware fixed shapes:
        // - olivesPerMinuteCeil (ElevenLabs Music — round-up-to-the-minute billing)
        // - olivesPerAsset + durationTiers (existing SFX shape)
        // - flat olivesPerAsset
        baseCost = resolveOlivesForDuration(pricing, duration)
      }
      variableCost = baseCost
    }

    const surcharges = audioSurcharge + referenceImageSurcharge
    const breakdown: CustomerListCostBreakdown = {
      total: variableCost + surcharges,
      unit: billableUnit,
      outputQuantity,
      videoReferenceQuantity,
      // Only when the list card bills the references (matches calculateGenerationCostBreakdown).
      hasVideoReferences: hasVideoReferences && billsVideoReferences,
      surcharges,
    }
    return {
      baseCost,
      variableCost,
      audioSurcharge,
      referenceImageSurcharge,
      referenceTokenSurchargeOlives,
      billedReferenceTokens,
      resolutionAdjustment,
      qualityAdjustment,
      breakdown,
    }
  }
  const served = listCosts(servedPricing)
  const { baseCost, audioSurcharge, referenceImageSurcharge, resolutionAdjustment, qualityAdjustment, breakdown } =
    served
  const rules = pickCustomerPricingRules(resolveCustomerPricingRules(model), resolution)
  // A percentage agreement is off the PUBLISHED list card, not the routed one
  // (docs/plans/negotiated-customer-pricing.md); the backend quotes the same basis.
  const publishedListCost =
    rules.some(rule => rule.kind === 'percentage') && model.id in MODEL_OLIVE_COSTS
      ? listCosts(replaceModelOliveCost(model.pricing, MODEL_OLIVE_COSTS[model.id as PricedModelId])).breakdown.total
      : undefined
  // Each batch member is submitted and charged independently. The shared
  // engine rounds the per-item quote exactly like the backend; total it after.
  // A cost-based rule prices from our provider cost, which stays on the server. The catalog carries the
  // rule's finished customer rates for members instead, so the estimate needs no request per change.
  const costPlus = model.customerPricing?.costPlus
  const costPlusRates = options.mediaInputsPresent ? (costPlus?.mediaInputRates ?? costPlus?.rates) : costPlus?.rates
  const costPlusCharge = costPlusRates
    ? estimateCostPlusCharge(
        costPlusRates,
        {
          quality: perSecondQuality ?? quality,
          resolution,
          hasVideoReferences: (videoReferenceCount ?? 0) > 0,
          durationSeconds: duration ?? null,
          videoReferenceSeconds: videoReferenceDuration ?? 0,
          ...(videoReferenceClipSeconds?.length ? { videoReferenceClipSeconds } : {}),
          promptChars: charCount ?? 0,
          aspectRatio: options.aspectRatio,
          imageReferenceCount: imageReferenceCount ?? 0,
          billedReferenceTokens: served.billedReferenceTokens,
          referenceTokenSurchargeOlives: served.referenceTokenSurchargeOlives,
        },
        breakdown.surcharges,
      )
    : null
  const { quote, pricedByAgreement } = quoteCustomerPriceInBrowser(
    breakdown,
    rules,
    { publishedListCost },
    costPlusCharge,
  )
  const total = roundOlives(quote.finalCharge * batchCount)

  return {
    estimatedCost: total,
    estimatedUsd: null,
    mode: 'olives',
    billedReferenceTokens: served.billedReferenceTokens,
    costBreakdown: {
      base: baseCost,
      audio: audioSurcharge > 0 ? audioSurcharge : undefined,
      referenceImages: referenceImageSurcharge > 0 ? referenceImageSurcharge : undefined,
      resolution: resolutionAdjustment !== 0 ? resolutionAdjustment : undefined,
      quality: qualityAdjustment !== 0 ? qualityAdjustment : undefined,
      batch: total,
    },
    quote,
    pricedByAgreement,
  }
}

/**
 * Hook for calculating generation costs with support for both video and image pricing models.
 *
 * Handles:
 * - Per-second pricing (video): olivesPerSecond × duration × batch
 * - Per-image pricing (image): olivesPerImage × batch
 * - Fixed / duration-tiered pricing (e.g. audio SFX)
 * - Audio surcharge (video): audioSurchargeOlives × duration
 * - Resolution multiplier (image): 1.5× for 2K, 2× for 4K
 * - Quality multiplier (image): driven by `pricing.qualityMultipliers` —
 *   only applied when the model declares the field, so non-quality models
 *   are unaffected by stray `quality` values.
 * - BYOK direct billing mode: when the active org has a BYOK key for the
 *   selected model provider, costs are paid directly to that provider.
 *
 * Web-search provider cost is intentionally absorbed by Martini and does not
 * affect the returned estimate.
 */
export function useCostEstimation(options: UseCostEstimationOptions): UseCostEstimationResult {
  const {
    model,
    modality,
    batchCount,
    duration,
    perSecondQuality,
    audioEnabled,
    voiceIds,
    charCount,
    videoReferenceCount,
    videoReferenceDuration,
    audioReferenceSeconds,
    videoReferenceClipSeconds,
    imageReferenceCount,
    imageReferenceMeasurements,
    mediaInputsPresent,
    resolution,
    quality,
    aspectRatio,
    enableWebSearch,
    byokPricing,
  } = options

  const isByokMode = !!(byokPricing && byokPricing.isByokForModel(model))
  const needsFalKey = !isByokMode && !!byokPricing?.needsFalKeyForModel(model)

  return useMemo((): UseCostEstimationResult => {
    const base = computeEstimatedCost({
      model,
      modality,
      batchCount,
      duration,
      perSecondQuality,
      audioEnabled,
      voiceIds,
      charCount,
      videoReferenceCount,
      videoReferenceDuration,
      audioReferenceSeconds,
      videoReferenceClipSeconds,
      imageReferenceCount,
      imageReferenceMeasurements,
      mediaInputsPresent,
      resolution,
      quality,
      aspectRatio,
      enableWebSearch,
    })
    // No olive price for a model the fal-key plan never charges olives for.
    if (model && needsFalKey) return { ...base, estimatedUsd: null, mode: 'fal_key_required' }
    if (!model || !isByokMode) return base

    // FAL bills output seconds plus the trimmed video-reference seconds on every card that
    // prices a clip (Seedance's videoInputDiscount, H3's absolute videoInputOlivesPerSecond), so
    // the BYOK quantity must include the reference duration too — otherwise a BYOK request with
    // references shows USD for output seconds only. Same card test as computeEstimatedCost.
    const byokBillableDuration =
      cardBillsVideoReferences(model.pricing) &&
      (videoReferenceCount ?? 0) > 0 &&
      typeof videoReferenceDuration === 'number'
        ? (duration ?? 0) + videoReferenceDuration
        : duration

    const estimatedUsd = computeByokUsd({
      byokPricing: byokPricing!,
      model,
      duration: byokBillableDuration,
      charCount,
      batchCount,
      resolution,
      perSecondQuality,
      quality,
      aspectRatio,
      imageReferenceCount,
      hasReferences:
        (imageReferenceCount ?? 0) > 0 ||
        (videoReferenceCount ?? 0) > 0 ||
        (audioReferenceSeconds?.length ?? 0) > 0 ||
        options.sourceVideoPresent === true,
      billedReferenceTokens: base.billedReferenceTokens,
      pricingType: model.pricing.type,
    })

    return {
      ...base,
      estimatedUsd,
      mode: 'byok_usd',
    }
  }, [
    model,
    modality,
    duration,
    perSecondQuality,
    batchCount,
    audioEnabled,
    voiceIds,
    charCount,
    videoReferenceCount,
    videoReferenceDuration,
    imageReferenceCount,
    options.sourceVideoPresent,
    imageReferenceMeasurements,
    mediaInputsPresent,
    resolution,
    quality,
    aspectRatio,
    enableWebSearch,
    isByokMode,
    needsFalKey,
    byokPricing,
    audioReferenceSeconds,
    videoReferenceClipSeconds,
  ])
}

function computeByokUsd(args: {
  byokPricing: UseBYOKPricingResult
  model: ModelInfo
  duration: number | undefined
  charCount: number | undefined
  batchCount: number
  resolution: string | null | undefined
  perSecondQuality?: string | null
  /** Per-image quality tier — lets `providerCostUsdByQuality` price low/medium below the default. */
  quality?: string | null
  aspectRatio?: string | null
  imageReferenceCount?: number
  hasReferences?: boolean
  billedReferenceTokens?: number
  pricingType: 'per_second' | 'per_image' | 'fixed'
}): number | null {
  const { byokPricing, model, duration, charCount, batchCount, resolution, perSecondQuality, quality, pricingType } =
    args
  const { aspectRatio, imageReferenceCount, hasReferences, billedReferenceTokens } = args
  // Per-image pricing is priced per unit (the batch is the quantity).
  // Per-second and fixed are priced per shot; multiply after by batchCount.
  if (pricingType === 'per_image') {
    return byokPricing.getUsdCost(model, {
      batchCount,
      resolution,
      perSecondQuality,
      quality,
      aspectRatio,
      imageReferenceCount,
      hasReferences,
    })
  }
  const perShot = byokPricing.getUsdCost(model, {
    duration: duration ?? null,
    charCount: charCount ?? null,
    batchCount: 1,
    resolution,
    perSecondQuality,
    aspectRatio,
    imageReferenceCount,
    hasReferences,
    billedReferenceTokens,
  })
  if (perShot == null) return null
  const raw = perShot * batchCount
  return Math.round(raw * 10000) / 10000
}
